import { Text } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { ReactNode } from 'react'

import { SectionCard } from '../SectionCard'
import { Tooltip } from '../Tooltip'

const MotionSectionCard = motion(SectionCard)

export interface IMetricCardProps {
  label: string
  value: string
  hint?: string
  tooltip?: string
  valueColor?: string
  delay?: number
  children?: ReactNode
}

/** Card de métrica dos relatórios de pesquisa: rótulo, número grande e uma linha de apoio. */
export default function MetricCard({
  label,
  value,
  hint,
  tooltip,
  valueColor = 'gray.900',
  delay = 0,
  children
}: IMetricCardProps) {
  // O tabIndex fica no elemento que o Tooltip envolve, e não num filho: o Chakra escuta o
  // foco no próprio filho que recebe, e o evento de focus não faz bubble — com o tabIndex
  // dentro, o tooltip abriria no hover mas nunca por teclado.
  const title = (
    <Text
      fontSize="12px"
      fontWeight="500"
      color="gray.500"
      mb="1"
      noOfLines={1}
      {...(tooltip
        ? {
            tabIndex: 0,
            cursor: 'help',
            display: 'inline-block',
            borderBottom: '1px dashed',
            borderColor: 'gray.300'
          }
        : {})}
    >
      {label}
    </Text>
  )

  return (
    <MotionSectionCard
      variant="tile"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay, ease: [0.22, 0.61, 0.36, 1] }}
      sx={{
        transition: 'all .2s ease',
        ':hover': { boxShadow: '0 8px 24px -8px rgba(17,24,39,0.1)', transform: 'translateY(-2px)' }
      }}
    >
      {tooltip ? <Tooltip label={tooltip}>{title}</Tooltip> : title}
      <Text
        fontSize="28px"
        fontWeight="700"
        color={valueColor}
        lineHeight="1.1"
        letterSpacing="-0.02em"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </Text>
      {hint && (
        <Text variant="caption" mt="1">
          {hint}
        </Text>
      )}
      {children}
    </MotionSectionCard>
  )
}
